import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import './NewProcessDialog.css';

const INVALID_CHARS = /[\\/:*?"<>|]/;

const NewProcessDialog = ({ isOpen, onConfirm, onCancel, title, icon, submitLabel, submitIcon, defaultName }) => {
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const inputRef = useRef(null);
  const formRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setName(defaultName || '');
      setError('');
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, defaultName]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      onCancel();
    }
  }, [onCancel]);

  useEffect(() => {
    const el = formRef.current;
    if (!el) return;
    el.addEventListener('keydown', handleKeyDown);
    return () => el.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  if (!isOpen) return null;

  const validate = (value) => {
    if (!value.trim()) return 'Bitte einen Prozessnamen eingeben.';
    if (INVALID_CHARS.test(value)) return String.raw`Ungültige Zeichen: \ / : * ? " < > |`;
    if (value.trim().length > 100) return 'Name darf maximal 100 Zeichen lang sein.';
    return '';
  };

  const handleChange = (e) => {
    const val = e.target.value;
    setName(val);
    setError(validate(val));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const err = validate(name);
    if (err) {
      setError(err);
      return;
    }
    onConfirm(name.trim());
  };

  return (
    <div className="new-process-overlay">
      <button type="button" className="new-process-overlay-btn" onClick={onCancel} aria-label="Dialog schließen" />
      <dialog className="new-process-dialog" open aria-label={title || 'Neuer Prozess'}>
        <div className="new-process-header">
          <span className="np-icon"><i className={icon || 'bi bi-file-earmark-plus-fill'}></i></span>
          <h5>{title || 'Neuer Prozess'}</h5>
        </div>
        <form ref={formRef} onSubmit={handleSubmit}>
          <div className="new-process-body">
            <label htmlFor="np-name-input">Prozessname</label>
            <input
              id="np-name-input"
              ref={inputRef}
              type="text"
              value={name}
              onChange={handleChange}
              placeholder="z.B. Login-Prozess"
              autoComplete="off"
            />
            {error && <div className="np-error">{error}</div>}
          </div>
          <div className="new-process-footer">
            <button type="submit" className="btn np-btn-create" disabled={!!error || !name.trim()}>
              <i className={submitIcon || 'bi bi-plus-circle'}></i> {submitLabel || 'Erstellen'}
            </button>
            <button type="button" className="btn np-btn-cancel" onClick={onCancel}>
              Abbrechen
            </button>
          </div>
        </form>
      </dialog>
    </div>
  );
};

NewProcessDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  title: PropTypes.string,
  icon: PropTypes.string,
  submitLabel: PropTypes.string,
  submitIcon: PropTypes.string,
  defaultName: PropTypes.string,
};

export default NewProcessDialog;
